import SoundFontPlayer from "soundfont-player";
import { GUITAR, parseChord, searchVoicings, tableName } from "./NoteBar/voicings";

/**
 * 악보 재생 반주 (2026-10, 새 디자인). 박마다 그 순간의 코드를 통기타 소리로 쳐 준다.
 * - 스트로크: 4/4 는 '다운 · 다운업 · 업다운업'(팝 기본), 3/4 는 '다운 · 다운업 · 다운업', 6/8 은 셋잇단 느낌
 * - 아르페지오: 베이스 → 줄 하나씩 (발라드)
 * 소리: FluidR3 통기타 사운드폰트 (/static/acoustic_guitar_steel-mp3.js, 무료 공개)
 */
export type AccStyle = "off" | "strum" | "arpeggio";

let ctx: AudioContext | null = null;
let player: any = null;
let loading: Promise<any> | null = null;

const ensure = async () => {
  if (player) return player;
  if (!loading) {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    ctx = ctx || new Ctx();
    loading = SoundFontPlayer.instrument(ctx as AudioContext, "/static/acoustic_guitar_steel-mp3.js" as any, { gain: 2.2 })
      .then((p: any) => (player = p))
      .catch(() => null);
  }
  return loading;
};

export const prepareAccompany = async () => {
  // 휴대폰은 터치한 그 순간에만 소리를 켤 수 있어서, 기다리기(await) 전에 바로 소리 장치를 만들고 깨운다
  if (!ctx) {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    ctx = new Ctx();
  }
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  await ensure();
  if (ctx && ctx.state === "suspended") await ctx.resume().catch(() => {});
};

// 코드 이름(표시 HTML) → 낮은 줄부터 MIDI 음 목록 (가장 많이 쓰는 운지법)
const voiceCache = new Map<string, number[]>();
const notesOf = (html: string): number[] => {
  if (voiceCache.has(html)) return voiceCache.get(html)!;
  const name = tableName(html.replace(/<sup>#<\/sup>/g, "#"));
  let notes: number[] = [];
  if (parseChord(name)) {
    const f = searchVoicings(name, GUITAR, 1)[0];
    if (f) notes = f.map((fr, i) => (fr === null ? -1 : GUITAR[i] + fr)).filter((n) => n > 0);
  }
  voiceCache.set(html, notes);
  return notes;
};

// 바로 앞 박에 친 코드 — 마디 중간에 코드가 바뀌면 그 박에도 베이스를 친다 (사장님 10/17)
let lastChord = "";

const hit = (notes: number[], when: number, dir: "down" | "up", gain: number, dur: number) => {
  if (!player || !notes.length) return;
  const order = dir === "down" ? notes : [...notes].reverse().slice(0, 4); // 업은 높은 줄 몇 개만, 약하게
  order.forEach((n, i) => {
    player.play(n, when + i * (dir === "down" ? 0.012 : 0.009), { gain, duration: dur });
  });
};

/**
 * 한 박 연주. beatIdx = 마디 안에서 몇 번째 박(0부터), beats = 한 마디 박 수, beatSec = 한 박 길이(초)
 */
export const playBeat = (chordHtml: string, beatIdx: number, beats: number, beatSec: number, style: AccStyle) => {
  if (style === "off" || !player || !ctx || !chordHtml) return;
  const notes = notesOf(chordHtml);
  if (!notes.length) return;
  const t0 = ctx.currentTime + 0.02;
  const half = beatSec / 2;
  const changed = chordHtml !== lastChord;
  lastChord = chordHtml;
  if (style === "arpeggio") {
    // 베이스(가장 낮은 음, 분수 코드면 / 뒤 음) → 나머지 줄을 반 박씩. 마디 첫 박과 코드가 바뀐 박은 베이스부터
    const bass = notes[0];
    const upper = notes.slice(1);
    const withBass = beatIdx === 0 || changed;
    const seq = withBass ? [bass, upper[1] ?? upper[0]] : [upper[(beatIdx * 2) % upper.length], upper[(beatIdx * 2 + 1) % upper.length]];
    seq.forEach((n, k) => n !== undefined && player.play(n, t0 + k * half, { gain: k === 0 && withBass ? 1.1 : 0.8, duration: beatSec * 2 }));
    return;
  }
  if (beats === 3) {
    hit(notes, t0, "down", beatIdx === 0 ? 1.0 : 0.7, beatSec * 1.5);
    if (beatIdx > 0) hit(notes, t0 + half, "up", 0.45, beatSec);
    return;
  }
  // 4/4: D | D U | (쉼) U | D U  — '다운 다운업 업다운업'
  const pattern: [boolean, boolean][] = [[true, false], [true, true], [false, true], [true, true]];
  const [down, up] = pattern[beatIdx % 4];
  if (down) hit(notes, t0, "down", beatIdx === 0 ? 1.0 : 0.75, beatSec * 1.4);
  if (up) hit(notes, t0 + half, "up", 0.45, beatSec);
};
